import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Box,
  Button,
  MenuItem,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import useTaxAnalytics from "../../hooks/useTaxAnalytics";

const currencyFmt = (v) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(v) || 0);

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC'
];

const toInputDate = (d) => {
  if (!d) return "";
  const iso = d.toISOString();
  return iso.slice(0, 10);
};

export default function TaxSummaryTable() {
  const now = new Date();
  const defaultFrom = new Date(now.getTime() - 29 * 864e5);

  const [from, setFrom] = React.useState(toInputDate(defaultFrom));
  const [to, setTo] = React.useState(toInputDate(now));
  const [stateFilter, setStateFilter] = React.useState("");

  const fromISO = from ? new Date(`${from}T00:00:00`).toISOString() : undefined;
  const toISO = to ? new Date(`${to}T23:59:59`).toISOString() : undefined;

  const { data, loading, error } = useTaxAnalytics({
    fromISO,
    toISO,
    state: stateFilter || undefined,
  });

  const totals = data?.totals || { taxableSales: 0, salesTax: 0, invoices: 0 };
  const rows = data?.rows || [];

  const clearFilters = () => {
    setFrom(toInputDate(defaultFrom));
    setTo(toInputDate(now));
    setStateFilter("");
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Taxable Sales & Sales Tax</Typography>
          <Button size="small" onClick={clearFilters}>Reset</Button>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              label="From"
              type="date"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="To"
              type="date"
              fullWidth
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="State"
              fullWidth
              variant="outlined"
              size="small"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {US_STATES.map((abbr) => (
                <MenuItem key={abbr} value={abbr}>{abbr}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box mt={3} mb={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <StatBox label="Taxable Sales" value={currencyFmt(totals.taxableSales)} loading={loading} />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatBox label="Sales Tax Collected" value={currencyFmt(totals.salesTax)} loading={loading} />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatBox label="Invoices" value={totals.invoices || 0} loading={loading} />
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Box mb={2} p={2} style={{ background: "#fffbeb", border: "1px solid #f59e0b", borderRadius: 8 }}>
            <Typography variant="body2" color="textSecondary">{error}</Typography>
          </Box>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Taxable Sales</TableCell>
                <TableCell align="right">Sales Tax</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, idx) => (
                  <TableRow key={idx}>
                    <TableCell><Skeleton width={80} /></TableCell>
                    <TableCell><Skeleton width={140} /></TableCell>
                    <TableCell><Skeleton width={90} /></TableCell>
                    <TableCell align="right"><Skeleton width={80} /></TableCell>
                    <TableCell align="right"><Skeleton width={80} /></TableCell>
                  </TableRow>
                ))
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow key={row._id || `${row.invoiceNumber}-${row.createdAt}`}>
                    <TableCell>{row.invoiceNumber}</TableCell>
                    <TableCell>{row.clientStore || row.clientName || "—"}</TableCell>
                    <TableCell>
                      {row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell align="right">{currencyFmt(row.taxable)}</TableCell>
                    <TableCell align="right">{currencyFmt(row.salesTax)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="textSecondary" align="center">
                      No invoices match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

function StatBox({ label, value, loading }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="caption" color="textSecondary">{label}</Typography>
        {loading ? <Skeleton width={120} /> : (
          <Typography variant="h6" style={{ fontWeight: 700 }}>{value}</Typography>
        )}
      </CardContent>
    </Card>
  );
}
